import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  Heading,
  Input,
  Link,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
  UnorderedList,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FaFile, FaTrashAlt } from 'react-icons/fa';
import { IoAddCircleOutline } from 'react-icons/io5';

import MainCard from '../../../components/MainCard/Index';
import MessageError from '../../../components/MessageError';

import { badgeColor } from '../../../utils/badgeFunction';
import { formatFileSize } from '../../../utils/functions';
import { validateMaxFile, validateTypeFile, validationSizeFile } from '../../../utils/validationForm';

import { useRequestMoreDetailResources } from '../../../services/hooks/Resources/useRequestMoreDetailResources';
import { IFile, useUpdateResource } from '../../../services/hooks/Resources/useUpdateResource';
import {
  IExaminationBoardResponse,
  useGetExaminationBoard,
} from '../../../services/hooks/ExaminationBoard/useGetExaminationBoard';
import { useCandidatefSubjects } from '../../../services/hooks/Resources/useCandidateSubjects';
import { useGetResourceTitleList } from '../../../services/hooks/Titles/useNewResourceTitle';

interface IFileList {
  id: string;
  name: string;
  size: number;
  url?: string;
  created_at?: string;
}

interface FormData {
  detail?: string;
  exemption?: string;
  attachmentFile?: FileList;
  proof_subject?: string;
  title_category?: string;
  title_item?: string;
  examination_board_id?: string;
  examination_board_member_id?: string;
  redome_code?: string;
  nis?: string;
  questionNumber?: string;
}

interface FileState {
  new: IFile[];
  old: IFileList[];
}

const MAX_OBSERVATION_PREVIEW = 800;

const parseDateTime = (value?: string): Date | null => {
  if (!value) return null;

  const [datePart, timePart] = value.trim().split(' ');
  if (!datePart || !timePart) return null;

  const [day, month, year] = datePart.split('/').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);

  const hasInvalidPart = [day, month, year, hour, minute].some(Number.isNaN);
  if (hasInvalidPart) return null;

  return new Date(year, month - 1, day, hour, minute);
};

const isResourcePeriodActive = (periodText?: string): boolean => {
  if (!periodText) return false;

  const parts = periodText
    .replace(/\s+/g, ' ')
    .trim()
    .split(/\s+at[eé]\s+/i);
  if (parts.length !== 2) return false;

  const start = parseDateTime(parts[0]);
  const end = parseDateTime(parts[1]);

  if (!start || !end) return false;

  const now = new Date();
  return now >= start && now <= end;
};

const mapNewFilesToPreview = (files: IFile[]): IFileList[] =>
  files.map((item) => ({
    id: item.id,
    name: item.file.name,
    size: item.file.size,
    url: URL.createObjectURL(item.file),
    created_at: '',
  }));

const DetailResource: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const showObservation = useDisclosure();
  const refInputFile = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const resourceId = location.pathname.split('/')[5];
  const resourcePeriodId = location.state?.periodId;
  const isEditMode = searchParams.get('mode') === 'edit' || location.state?.mode === 'edit';

  const [files, setFiles] = useState<FileState>({
    new: [],
    old: [],
  });

  const { data, refetch, isLoading } = useRequestMoreDetailResources(resourceId);
  const { mutateAsync, isPending } = useUpdateResource(resourceId);
  const subjectData = useCandidatefSubjects({
    selective_process_id: data?.selective_process.id || '',
    registration_id: data?.registration_id || '',
  });
  const titlesList = useGetResourceTitleList({
    remove_pagination: true,
    registrationId: data?.registration_id || '',
  });

  const filesList = useMemo(() => {
    return [...mapNewFilesToPreview(files.new), ...files.old];
  }, [files]);

  const examinationBoardList = useGetExaminationBoard<IExaminationBoardResponse[]>({
    remove_pagination: true,
    selective_process: data?.selective_process.id,
    status: 'published',
    show_members: true,
    resource_period_id: data?.resource_period_id,
  });

  const buildValidationSchema = (
    hasQuestion?: boolean,
    againstExaminationBoard?: boolean,
    files?: IFileList[],
  ) =>
    Yup.object({
      detail: Yup.string(),
      questionNumber: Yup.string().when('proof_subject', {
        is: () => hasQuestion,
        then: (schema) => schema.required('Campo obrigatório'),
      }),
      attachmentFile: Yup.mixed<FileList>()
        .when({
          is: () => files?.length === 0 && !!data?.resource_rules?.is_required_attachment,
          then: (schema) => schema.required('Envio de arquivo obrigatório'),
        })
        .test('invalidExtension', 'Verifique o nome do arquivo enviado', (file) => {
          const listFile = file as FileList;
          let hasError = true;

          for (let index = 0; index < listFile?.length; index++) {
            const hasDot = listFile[index].name.split('.');
            if (hasDot.length > 2) hasError = false;
          }

          return hasError;
        })
        .test(
          'validationSizeFile',
          `Tamanho máximo para arquivos é de ${data?.resource_rules?.size_attached}MB`,
          (file) => {
            const listFile = file as FileList;

            if (!data?.resource_rules?.has_attachment) return true;

            if (listFile?.length > 0) {
              const response = validationSizeFile(listFile, data?.resource_rules?.size_attached);
              return response;
            }

            return true;
          },
        )
        .test(
          'validationMaxFile',
          `Quantidade máxima de arquivos é de ${data?.resource_rules?.max_attached} arquivos`,
          (file) => {
            const listFile = file as FileList;
            if (!data?.resource_rules?.has_attachment) return true;

            if (listFile?.length > 0) {
              return validateMaxFile(listFile, data?.resource_rules?.max_attached);
            }
            return true;
          },
        )
        .test('validationTypeFile', 'Formato de arquivo não suportado', (file) => {
          const listFile = file as FileList;
          if (!data?.resource_rules?.has_attachment) return true;
          if (listFile?.length > 0) {
            return validateTypeFile(listFile);
          }
          return true;
        }),
      proof_subject: hasQuestion ? Yup.string().required('Campo obrigatório') : Yup.string(),
      examination_board_id: againstExaminationBoard ? Yup.string().required('Campo obrigatório') : Yup.string(),
      examination_board_member_id: Yup.string(),
      nis: Yup.string().when({
        is: () => data?.exemption?.type.name?.toLowerCase().includes('cadúnico'),
        then: (schema) => schema.required('Campo obrigatório').length(11, 'Campo NIS deve conter 11 digitos'),
      }),
      redome_code: Yup.string().when({
        is: () => data?.exemption?.type.name?.toLowerCase().includes('medula'),
        then: (schema) => schema.required('Campo obrigatório'),
      }),
      title_category: Yup.string().when({
        is: () => data?.resource_type?.against_title,
        then: (schema) => schema.required('Campo obrigatório'),
      }),
      title_item: Yup.string().when({
        is: () => data?.resource_type?.against_title,
        then: (schema) => schema.required('Campo obrigatório'),
      }),
    });

  const validationSchema = useMemo(
    () =>
      buildValidationSchema(
        data?.resource_type?.has_question,
        data?.resource_type?.against_examination_board,
        filesList,
      ),
    [data, filesList],
  );

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
  });

  const observation = data?.observation ?? '';

  const truncatedObservation = useMemo(() => {
    if (observation.length <= MAX_OBSERVATION_PREVIEW) return observation;
    return observation.slice(0, MAX_OBSERVATION_PREVIEW).replace(/\s+\S*$/, '');
  }, [observation]);

  const isPeriodActive = useMemo(
    () => isResourcePeriodActive(data?.resource_period_date),
    [data?.resource_period_date],
  );

  const colorBadge = useMemo(() => badgeColor(data?.status.name || ''), [data?.status.name]);
  const colorBadgeExemption = useMemo(
    () => badgeColor(data?.exemption?.status?.name || ''),
    [data?.exemption?.status?.name],
  );

  // const selectedExaminationBoard = useMemo(
  //   () => examinationBoardList?.data?.find((item) => item.id === watch('examination_board_id')),
  //   [examinationBoardList?.data, watch('examination_board_id')],
  // );

  useEffect(() => {
    return () => {
      files.new.forEach((item) => {
        const previewUrl = URL.createObjectURL(item.file);
        URL.revokeObjectURL(previewUrl);
      });
    };
  }, [files.new]);

  useEffect(() => {
    if (!resourceId) navigate(-1);
  }, [navigate, resourceId]);

  useEffect(() => {
    if (!data) return;

    reset({
      detail: data?.observation || '',
      proof_subject: data?.proof_subject?.id || '',
      title_category: data?.titles?.category?.id || '',
      title_item: data?.titles?.item?.id || '',
      examination_board_id: data?.examination_board?.id || '',
      examination_board_member_id: data?.examination_board?.member?.id || '',
      nis: data?.candidate?.nis || '',
      redome_code: data?.candidate?.redome_code || '',
      exemption: data?.exemption?.id || '',
      questionNumber: data?.question || '',
    });

    setFiles({
      old: data.attachments?.resource_file || [],
      new: [],
    });
  }, [data, examinationBoardList.data]);

  useEffect(() => {
    if (location.state?.mode === 'edit' && searchParams.get('mode') !== 'edit') {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('mode', 'edit');
          return next;
        },
        { replace: true },
      );
    }
  }, [location.state?.mode, searchParams]);

  const exitEditMode = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('mode');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (!isEditMode) return;

    if (data && !isPeriodActive) {
      exitEditMode();
      toast({
        title: 'Atenção',
        description: 'O período de recursos não está ativo. Não é possível editar.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [exitEditMode, isEditMode, isPeriodActive, toast]);

  const goBackToResourceList = useCallback(() => {
    navigate(-1);
  }, [navigate, resourceId, resourcePeriodId]);

  const openEditMode = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('mode', 'edit');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const handleFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []).map((file) => ({
      id: crypto.randomUUID(),
      file,
    }));

    if (refInputFile.current) {
      refInputFile.current.value = '';
    }

    setFiles((prev) => ({
      ...prev,
      new: [prev.new, ...selectedFiles].flat(),
    }));
  }, []);

  const handleRemoveFile = useCallback((fileId: string) => {
    setFiles((prev) => ({
      old: prev.old.filter((item) => item.id !== fileId),
      new: prev.new.filter((item) => item.id !== fileId),
    }));
  }, []);

  const validateSubmit = useCallback(
    (payload: FieldValues) => {
      if (!isPeriodActive) {
        toast({
          title: 'Atenção',
          description: 'Período encerrado. Alterações não são permitidas.',
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
        return false;
      }

      const hasFiles = files.new.length > 0 || files.old.length > 0;
      const hasDetail = typeof payload.detail === 'string' && payload.detail.trim().length > 0;

      if (!hasFiles && !hasDetail) {
        toast({
          title: 'Atenção',
          description: 'Informe uma justificativa ou anexe arquivos para salvar.',
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
        return false;
      }

      if ((data?.attachments?.resource_file?.length || 0) > 5) {
        toast({
          title: 'Erro',
          description: 'Limite de anexos excedido',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return false;
      }

      return true;
    },
    [data?.attachments?.resource_file?.length, files.new.length, files.old.length, isPeriodActive, toast],
  );

  const updateResource = useCallback(
    async (payload: FieldValues) => {
      if (!validateSubmit(payload)) return;

      const removeAttachmentFileIds =
        data?.attachments?.resource_file
          ?.filter((item) => !files.old.some((file) => file.id === item.id))
          .map((item) => item.id) || [];

      try {
        await mutateAsync({
          attachmentFile: files.new,
          removeAttachmentFileIds,
          payload,
        });

        await refetch();

        toast({
          title: 'Sucesso',
          description: 'Recurso atualizado com sucesso',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });

        reset({
          attachmentFile: undefined,
          detail: payload.detail || data?.observation || '',
          proof_subject: data?.proof_subject?.id || '',
          title_category: data?.titles?.category?.id || '',
          title_item: data?.titles?.item?.id || '',
          examination_board_id: data?.examination_board?.id || '',
          examination_board_member_id: 'e2bde879-ce42-44b5-af96-efc7aef853d1',
        });

        goBackToResourceList();
      } catch (error: any) {
        toast({
          title: 'Erro',
          description: error?.response?.data?.message || 'Erro ao atualizar o recurso, tente novamente',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [data, files, goBackToResourceList, mutateAsync, refetch, reset, toast, validateSubmit],
  );

  const selectedTitle = titlesList?.data?.find((item) => item.id === watch('title_category'));

  if (isLoading && examinationBoardList.isLoading) {
    return (
      <MainCard height="100%" title="Recursos">
        <Flex align="center" justify="center" height="100%">
          <Spinner size="xl" />
        </Flex>
      </MainCard>
    );
  }

  if (!data) {
    return (
      <MainCard height="100%" title="Recursos">
        <Flex align="center" justify="center" height="100%">
          <Text>Nenhum dado encontrado.</Text>
        </Flex>
      </MainCard>
    );
  }

  console.log(errors);

  return (
    <MainCard height="100%" title="Recursos">
      <Stack px={['10px', '10px', '30px', '50px', '50px']} gap={7}>
        <Flex
          flexDirection={['column', 'column', 'column', 'row']}
          alignItems={['initial', 'initial', 'initial', 'center']}
        >
          <Stack direction="row" justifyContent="center" spacing={2}>
            {isEditMode ? (
              <Button
                size={['xs', 'xs', 'sm']}
                color="darkGrey"
                bg="white"
                border="1px solid"
                borderColor="darkGrey"
                onClick={exitEditMode}
                _hover={{ bg: 'gray.100', transform: 'translateY(-1px)', boxShadow: 'md' }}
                _active={{ transform: 'translateY(0)', boxShadow: 'sm' }}
                transition="all 0.2s"
                fontWeight="medium"
                py={3}
                w="100%"
                maxW="400px"
                lineHeight="normal"
              >
                Ver detalhes
              </Button>
            ) : (
              isPeriodActive && (
                <Button
                  size={['xs', 'xs', 'sm']}
                  color="white"
                  bg="colorLink"
                  onClick={openEditMode}
                  _hover={{ bg: 'blue.600', transform: 'translateY(-1px)', boxShadow: 'md' }}
                  _active={{ transform: 'translateY(0)', boxShadow: 'sm' }}
                  transition="all 0.2s"
                  fontWeight="medium"
                  lineHeight="normal"
                  py={3}
                  w="100%"
                  maxW="400px"
                >
                  Modificar recurso
                </Button>
              )
            )}
          </Stack>
        </Flex>

        <Card>
          <CardHeader borderBottom="1px solid" borderColor="#d6d6d6">
            <Flex alignItems="center" justifyContent="space-between">
              <Heading size="sm">RESUMO DO RECURSO #{data.resource_code}</Heading>
              <Badge
                h="fit-content"
                w="fit-content"
                color={colorBadge?.color}
                bgColor={colorBadge?.bgColor}
                fontSize={['0.55rem', '0.6rem', 'xs']}
                lineHeight="normal"
                p={[0, 1, 1, 1]}
                rounded={[7, 10, 13]}
                px={[1.5, 2.5, 3.5, 4]}
              >
                {data.status.name}
              </Badge>
            </Flex>
          </CardHeader>

          <CardBody>
            <Stack gap={3}>
              <Text>
                <strong>Inscrição:</strong> {data.registration_code || ''}
              </Text>
              <Text>
                <strong>Período:</strong> {data.resource_period_date || ''}
              </Text>
              <Text>
                <strong>Tipo do recurso:</strong> {data.resource_type.name}
              </Text>
              <Text>
                <strong>Nome:</strong> {data.candidate.name || ''}
              </Text>
              <Text>
                <strong>Processo seletivo:</strong> {data.selective_process?.name || ''}
              </Text>
              {data.vacancy.name && (
                <Text>
                  <strong>Vaga:</strong> {data.vacancy.name || ''}
                </Text>
              )}

              {data.proof_subject && (
                <Text>
                  <strong>Prova:</strong> {data.proof_subject?.name || '--'}
                </Text>
              )}

              {data.question && (
                <Text>
                  <strong>Questão:</strong> {data.question}
                </Text>
              )}
            </Stack>
          </CardBody>
        </Card>

        {data.examination_board && (
          <Card>
            <CardHeader borderBottom="1px solid" borderColor="#d6d6d6">
              <Heading size="sm">BANCA EXAMINADORA</Heading>

              {(!data.resource_type.against_examination_board ||
                (data.resource_type.against_examination_board && !isEditMode)) && (
                <Heading fontSize="11px" mt={1} color="gray" textTransform="uppercase">
                  {data.examination_board.name}
                </Heading>
              )}
            </CardHeader>

            <CardBody>
              <Stack gap={3}>
                {!data.resource_type.against_examination_board || !isEditMode ? (
                  <>
                    <Text>
                      <strong>Unidade acadêmica:</strong> {data.examination_board.academic_unit || ''}
                    </Text>
                    <Text>
                      <strong>Membro da banca:</strong> {data.examination_board.member?.colaborator_data?.name}
                    </Text>
                  </>
                ) : (
                  <Stack w={['100%', '100%', '100%', '50%', '50%']}>
                    <FormControl display="flex" flexDir="column">
                      <Text fontWeight="bold" mr={2}>
                        Selecione a banca para recurso:
                      </Text>

                      <Select
                        {...register('examination_board_id', {
                          onChange: () => {
                            reset({
                              ...watch(),
                              examination_board_member_id: '',
                            });
                          },
                        })}
                      >
                        <option value="" hidden>
                          Selecione
                        </option>

                        {examinationBoardList?.data?.length === 0 && <option value="">Sem bancas encontradas</option>}

                        {examinationBoardList?.data?.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <MessageError>{errors.examination_board_id?.message}</MessageError>

                    {/* {selectedExaminationBoard && (
                      <>
                        <FormControl display="flex" flexDir="column">
                          <Text fontWeight="bold" mr={2}>
                            Selecione o membro da banca:
                          </Text>

                          <Select {...register('examination_board_member_id')}>
                            <option value="">Selecione</option>

                            {selectedExaminationBoard.members
                              ?.filter((item) => {
                                const role = item?.role_display?.toLowerCase() || '';
                                return role.includes('presidente') || role.includes('efetivo');
                              })
                              .map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item?.colaborator_name || item?.colaborator_name || 'Nome não disponível'}
                                </option>
                              ))}
                          </Select>
                        </FormControl>

                        <MessageError>{errors.examination_board_member_id?.message}</MessageError>
                      </>
                    )} */}
                  </Stack>
                )}
              </Stack>
            </CardBody>
          </Card>
        )}

        {data.resource_type.has_exemption && (
          <Card>
            <CardHeader borderBottom="1px solid" borderColor="#d6d6d6">
              <Flex alignItems="center" justifyContent="space-between">
                <Heading size="sm">{data.exemption.type.name}</Heading>
                <Badge
                  h="fit-content"
                  w="fit-content"
                  color={colorBadgeExemption?.color}
                  bgColor={colorBadgeExemption?.bgColor}
                  fontSize={['0.55rem', '0.6rem', 'xs']}
                  lineHeight="normal"
                  p={[0, 1, 1, 1]}
                  rounded={[7, 10, 13]}
                  px={[1.5, 2.5, 3.5, 4]}
                >
                  {data.exemption.status.name}
                </Badge>
              </Flex>
            </CardHeader>

            <CardBody>
              {!data.resource_type.has_exemption || !isEditMode ? (
                <Stack gap={5}>
                  <Text>
                    <strong>NIS: </strong>
                    {data.candidate.nis || '--'}
                  </Text>
                </Stack>
              ) : (
                <Stack gap={5}>
                  {watch('exemption') && data.exemption && (
                    <Stack>
                      <Text>
                        <strong>Resposta à solicitação de isenção: </strong>
                        <Text as="span" dangerouslySetInnerHTML={{ __html: data.exemption?.justification }} />
                      </Text>

                      {/* {ex?.attachements && ex.attachements?.length > 0 && (
                    <Stack>
                      <Text fontWeight="bold">Anexos enviados ao solicitar isenção</Text>
                      <UnorderedList styleType={"''"}>
                        {ex?.attachements?.map((attach) => (
                          <ListItem key={attach?.id}>
                            <Flex color="colorLink" align="center" gap={1}>
                              <IoLink />
                              <Link fontSize="14px" href={attach?.url} target="_blank">
                                {attach?.name}
                              </Link>
                              <Text color="black">- {attach?.created_at}</Text>
                            </Flex>
                          </ListItem>
                        ))}
                      </UnorderedList>
                    </Stack>
                  )} */}
                    </Stack>
                  )}
                  {data.exemption && data.exemption.type?.name?.toLowerCase()?.includes('cadúnico') && (
                    <Stack w={['100%', '100%', '100%', '50%', '50%']}>
                      <FormControl display="flex" flexDir="row" alignItems="center">
                        <Text fontWeight="bold" mr={2}>
                          NIS:{' '}
                        </Text>
                        <Input {...register('nis')} type="text" />
                      </FormControl>
                      <MessageError>{errors.nis?.message}</MessageError>
                    </Stack>
                  )}

                  {data.exemption && data.exemption.type?.name?.toLowerCase()?.includes('medula') && (
                    <Stack w={['100%', '100%', '100%', '50%', '50%']}>
                      <FormControl display="flex" flexDir="column">
                        <Text fontWeight="bold" mr={2}>
                          Código REDOME:{' '}
                        </Text>
                        <Input {...register('redome_code')} type="text" />
                      </FormControl>
                      <MessageError>{errors.redome_code?.message}</MessageError>
                    </Stack>
                  )}
                </Stack>
              )}
            </CardBody>
          </Card>
        )}

        {data.resource_type.has_question && isEditMode && (
          <Card>
            <CardHeader borderBottom="1px solid" borderColor="#d6d6d6">
              <Flex alignItems="center" justifyContent="space-between">
                <Heading size="sm">Dados da questão</Heading>
              </Flex>
            </CardHeader>

            <CardBody>
              <Stack gap={5}>
                <Stack w={['100%', '100%', '100%', '50%', '50%']}>
                  <FormControl display="flex" flexDir="column" alignItems="flex-start">
                    <Text fontWeight="bold" mb={2}>
                      Selecione a prova:
                    </Text>
                    <Select {...register('proof_subject')} w="100%">
                      <option value={''} hidden>
                        Selecione a prova
                      </option>
                      {subjectData?.data?.map((subject) => (
                        <option key={subject?.id} value={subject?.id}>
                          {subject?.name}
                        </option>
                      ))}
                    </Select>
                    <MessageError>{errors?.proof_subject?.message}</MessageError>
                  </FormControl>
                  <MessageError>{errors.nis?.message}</MessageError>
                </Stack>

                <Stack w={['100%', '100%', '100%', '50%', '50%']}>
                  <FormControl display="flex" flexDir="column" alignItems="flex-start">
                    <Text fontWeight="bold" mb={2}>
                      Informe o número da questão:
                    </Text>

                    <Select {...register('questionNumber')} w="100%" disabled={!watch('proof_subject')}>
                      <option value={''}>
                        {!watch('proof_subject') ? 'Primeiro selecione a prova...' : 'Selecione...'}
                      </option>
                      {subjectData?.data
                        ?.filter((e) => e.id === watch('proof_subject'))
                        .map((subject) => {
                          // Se não tem questions, mostrar mensagem
                          if (!subject.questions || subject.questions.length === 0) {
                            return (
                              <option key="no-questions" value="" disabled>
                                Nenhuma questão disponível para esta prova
                              </option>
                            );
                          }
                          return subject.questions.map((q, index) => (
                            <option key={index} value={q}>
                              {q}
                            </option>
                          ));
                        })}
                    </Select>
                    {watch('proof_subject') &&
                      subjectData?.data?.find((s) => s.id === watch('proof_subject'))?.questions?.length === 0 && (
                        <Text fontSize="xs" color="orange.500" mt={1}>
                          ⚠️ Não há questões cadastradas para esta prova. Contate o administrador.
                        </Text>
                      )}
                    {/* <Input {...register('questionNumber')} type="number" w="100%" /> */}
                    <MessageError>{errors.questionNumber?.message}</MessageError>
                  </FormControl>
                  <MessageError>{errors.redome_code?.message}</MessageError>
                </Stack>
              </Stack>
            </CardBody>
          </Card>
        )}

        {data?.resource_type?.against_title && (
          <Card>
            <CardHeader borderBottom="1px solid" borderColor="#d6d6d6">
              <Heading size="sm">Envio de título</Heading>
            </CardHeader>
            <CardBody>
              {!data.resource_type.against_title || !isEditMode ? (
                <Stack gap={2}>
                  <Text>
                    <strong>Categoria: </strong>
                    {data.titles?.category?.name || '--'}
                  </Text>
                  <Text>
                    <strong>Item: </strong>
                    {data.titles?.item?.name || '--'}
                  </Text>
                </Stack>
              ) : (
                <Stack gap={5}>
                  <FormControl display="flex" flexDir="column">
                    <Text fontWeight="bold" mr={2}>
                      Selecione a categoria do título:
                    </Text>
                    <Select {...register('title_category')}>
                      <option value={''} hidden>
                        Selecione
                      </option>
                      {titlesList?.data?.length === 0 && <option value={''}>Sem titulos encontrados</option>}
                      {titlesList?.data?.map((item) => (
                        <option key={item?.id} value={item?.id}>
                          {item?.name?.toUpperCase()}
                        </option>
                      ))}
                    </Select>
                    <MessageError>{errors.title_category?.message}</MessageError>
                  </FormControl>

                  {selectedTitle && (
                    <FormControl display="flex" flexDir="column">
                      <Text fontWeight="bold" mr={2}>
                        Selecione o critério:
                      </Text>
                      <Select
                        {...register('title_item')}
                        isDisabled={
                          !watch('title_category') || !selectedTitle?.items || selectedTitle?.items.length === 0
                        }
                      >
                        <option value={''} hidden>
                          Selecione
                        </option>
                        {selectedTitle?.items?.length === 0 && <option value={''}>Sem titulos encontrados</option>}
                        {selectedTitle?.items?.map((item) => (
                          <option key={item?.id} value={item?.id}>
                            {item?.name?.toUpperCase()}
                          </option>
                        ))}
                      </Select>
                      <MessageError>{errors.title_item?.message}</MessageError>
                    </FormControl>
                  )}
                </Stack>
              )}
            </CardBody>
          </Card>
        )}

        <Stack as="form" id="resource-edit-form" onSubmit={handleSubmit(updateResource)}>
          <Card>
            <CardHeader borderBottom="1px solid" borderColor="#d6d6d6">
              <Heading size="sm">Justificativa do recurso</Heading>
            </CardHeader>

            <CardBody>
              {isEditMode && isPeriodActive ? (
                <Stack>
                  <Textarea
                    {...register('detail')}
                    maxLength={5000}
                    bg="white"
                    placeholder="Atualize sua justificativa"
                    minH="250px"
                  />
                  <Text textAlign="right">{watch('detail')?.length || 0}/5000 caracteres</Text>
                </Stack>
              ) : (
                <Text whiteSpace="pre-wrap">
                  {observation.length > 1000 ? (
                    <>
                      {showObservation.isOpen ? observation : truncatedObservation}{' '}
                      <Text
                        as="span"
                        onClick={showObservation.onToggle}
                        color="blue.500"
                        fontWeight="medium"
                        cursor="pointer"
                      >
                        Ver {showObservation.isOpen ? 'menos' : 'mais...'}
                      </Text>
                    </>
                  ) : (
                    observation || '--'
                  )}
                </Text>
              )}
            </CardBody>
          </Card>

          <Card mt={4}>
            <CardHeader py={3} borderBottom="1px solid" borderColor="#d6d6d6">
              <Flex justifyContent="space-between" alignItems="center">
                <Heading size="sm">Documentos anexados pelo candidato</Heading>

                {isEditMode && isPeriodActive && (
                  <Text as="label" htmlFor="file-input">
                    <IoAddCircleOutline cursor="pointer" color="green" size={28} />
                    <Input ref={refInputFile} hidden multiple id="file-input" type="file" onChange={handleFileChange} />
                  </Text>
                )}
              </Flex>
            </CardHeader>

            <CardBody>
              <Stack>
                {isEditMode && isPeriodActive && (
                  <Text fontSize="sm" color="gray.600">
                    Selecione todos os documentos que deseja enviar de uma só vez. Por exemplo, se forem 4, selecione os
                    4 arquivos juntos antes de enviar.
                  </Text>
                )}
                <UnorderedList ml={0} spacing={3}>
                  {filesList.length === 0 && <Text>Sem anexos</Text>}

                  {filesList.map((attach, index) => (
                    <Flex
                      key={attach.id}
                      border="1px solid #6db8ee"
                      rounded="5px"
                      p={4}
                      justifyContent="space-between"
                      bgColor="#ebf2f7"
                      alignItems="center"
                      pr={6}
                    >
                      <Link _hover={{ textDecoration: 'none' }} target="_blank" href={attach.url}>
                        <Flex gap={4} alignItems="center">
                          <Box rounded="full">
                            <FaFile color="#6db8ee" size={28} />
                          </Box>

                          <Stack gap={0}>
                            <Text
                              color="gray.700"
                              display={['none', 'none', 'none', 'flex']}
                              fontSize="sm"
                              fontWeight="bold"
                            >
                              {attach.name}
                            </Text>

                            <Text
                              display={['flex', 'flex', 'flex', 'none']}
                              color="gray.700"
                              fontSize="sm"
                              fontWeight="bold"
                            >
                              Arquivo {index + 1}
                            </Text>

                            <Flex gap={[0, 0, 2]} flexDirection={['column', 'column', 'row']}>
                              <Text fontSize="xs" color="gray.500" as="span">
                                <strong>Tamanho:</strong> {formatFileSize(attach.size)}
                              </Text>

                              {attach.created_at && (
                                <>
                                  <Box
                                    display={['none', 'none', 'flex']}
                                    h="15px"
                                    mt="2px"
                                    w="1px"
                                    bgColor="gray.500"
                                  />
                                  <Text fontSize="xs" color="gray.500" as="span">
                                    <strong>Data:</strong> {attach.created_at}
                                  </Text>
                                </>
                              )}
                            </Flex>
                          </Stack>
                        </Flex>
                      </Link>

                      {filesList.length > 1 && isEditMode && isPeriodActive && (
                        <Box>
                          <FaTrashAlt
                            onClick={() => handleRemoveFile(attach.id)}
                            color="red"
                            size={17}
                            cursor="pointer"
                          />
                        </Box>
                      )}
                    </Flex>
                  ))}
                </UnorderedList>

                {errors.attachmentFile && (
                  <Text color="red.500" fontSize="sm">
                    {errors.attachmentFile.message}
                  </Text>
                )}

                {filesList.length === 1 && isEditMode && isPeriodActive && (
                  <Alert status="info" mt={4} borderRadius="5px">
                    <AlertIcon boxSize="25px" />
                    <Stack gap={0} ml={2}>
                      <AlertTitle>Mínimo de um anexo necessário</AlertTitle>
                      <AlertDescription>
                        Só pode ser excluído um arquivo caso haja mais de um anexo. Caso queira excluir um arquivo, mas
                        só haja um anexo, adicione um novo anexo e depois exclua o arquivo desejado. O sistema não
                        permite que um recurso fique sem anexos. As alterações só serão salvas ao clicar no botão
                        &quot;Salvar alterações&quot;.
                      </AlertDescription>
                    </Stack>
                  </Alert>
                )}
              </Stack>
            </CardBody>
          </Card>

          {!isEditMode && (
            <Card mt={4}>
              <CardHeader py={3} borderBottom="1px solid" borderColor="#d6d6d6">
                <Heading size="sm">Resposta da análise do recurso</Heading>
              </CardHeader>

              <CardBody>
                <Stack gap={1}>
                  <Text>
                    <strong>Situação: </strong>
                    {data.status?.name}
                  </Text>

                  {data.proof_link && (
                    <Text>
                      <strong>Prova do candidato: </strong>
                      <Link
                        href={data.proof_link}
                        target="_blank"
                        color="colorLink"
                        _hover={{ textDecoration: 'underline' }}
                      >
                        Visualizar prova
                      </Link>
                    </Text>
                  )}

                  {data.justification && (
                    <Stack spacing={1}>
                      <Text fontWeight="bold">Justificativa da análise:</Text>
                      <Text dangerouslySetInnerHTML={{ __html: data.justification }} />
                    </Stack>
                  )}

                  {data.ccv_response && (
                    <Stack spacing={1}>
                      <Text fontWeight="bold">Manifestação complementar da CCV:</Text>
                      <Text dangerouslySetInnerHTML={{ __html: data.ccv_response }} />
                    </Stack>
                  )}
                </Stack>
              </CardBody>
            </Card>
          )}

          <Stack mt={5} mb={8} align="flex-end">
            <Stack direction="row" spacing={3}>
              {isEditMode && (
                <Button
                  color="white"
                  bg="green.500"
                  w="fit-content"
                  type="submit"
                  form="resource-edit-form"
                  isDisabled={isPending}
                  _hover={{ bg: 'green.600', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                  _active={{ transform: 'translateY(0)', boxShadow: 'md' }}
                  _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
                  transition="all 0.2s"
                  fontWeight="medium"
                  boxShadow="md"
                >
                  {isPending ? <Spinner /> : 'Salvar alterações'}
                </Button>
              )}

              <Button
                color="white"
                bg="colorLink"
                boxShadow="md"
                w="fit-content"
                onClick={goBackToResourceList}
                _hover={{ bg: 'blue.600', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                _active={{ transform: 'translateY(0)', boxShadow: 'md' }}
                transition="all 0.2s"
                fontWeight="medium"
              >
                Voltar
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </MainCard>
  );
};

export default DetailResource;
